import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Form, Input, InputNumber, Select, Switch, Button, Row, Col, Divider, Typography } from '@/shared/ui/primitives';
import { useTranslation } from 'react-i18next';
import { CitySelect, DistrictSelect } from '@/shared/ui/entity-selects';
import { propertyAmenityApi } from '@/features/property-amenities/api/propertyAmenity.api';
import { nationalityApi } from '@/features/nationalities/api/nationality.api';

export default function RoomAdForm({ initialData, onSubmit, isLoading, isCreate = false }: any) {
  const [form] = Form.useForm();
  const { t } = useTranslation();

  const statusOptions = ['DRAFT', 'PUBLISHED', 'ARCHIVED', 'REJECTED'].map((v) => ({ value: v, label: t(`roomAds.${v}`) }));
  const advertiserOptions = ['PROPERTY_OWNER', 'ROOMMATE', 'REAL_ESTATE_AGENT'].map((v) => ({ value: v, label: t(`roomAds.${v}`) }));
  const propertyOptions = ['APARTMENT', 'VILLA'].map((v) => ({ value: v, label: t(`roomAds.${v}`) }));
  const agentOptions = ['COMPANY', 'INDIVIDUAL', 'NONE'].map((v) => ({ value: v, label: t(`roomAds.${v}`) }));
  const stayOptions = ['NOT_SPECIFIED', 'UNLIMITED', 'ONE_MONTH', 'TWO_MONTHS', 'THREE_MONTHS', 'FOUR_MONTHS', 'FIVE_MONTHS', 'SIX_MONTHS', 'SEVEN_MONTHS', 'EIGHT_MONTHS', 'NINE_MONTHS', 'TEN_MONTHS', 'ELEVEN_MONTHS'].map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const daysOptions = ['ALL_WEEK', 'SUNDAY_TO_THURSDAY', 'WEEKENDS_ONLY'].map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const genderOptions = ['MALE', 'FEMALE', 'NO_PREFERENCE'].map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const smokingOptions = ['SMOKER', 'NON_SMOKER', 'NO_PREFERENCE'].map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const occupationOptions = ['STUDENT', 'EMPLOYEE', 'NO_PREFERENCE'].map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const languageOptions = ['ENGLISH', 'ARABIC', 'ARABIC_AND_ENGLISH'].map((v) => ({ value: v, label: t(`enum.${v}`, { defaultValue: v }) }));
  const billOptions = [
    { value: 'INCLUDED', label: t('roomAds.fields.billIncluded') },
    { value: 'NOT_INCLUDED', label: t('roomAds.fields.billNotIncluded') },
  ];
  const rentTypeOptions = [
    { value: 'MONTHLY', label: t('roomAds.fields.rentMonthly') },
    { value: 'YEARLY', label: t('roomAds.fields.rentYearly') },
  ];
  const roomTypeOptions = [
    { value: 'PRIVATE_BATHROOM', label: t('roomAds.fields.roomPrivateBath') },
    { value: 'SHARED_BATHROOM', label: t('roomAds.fields.roomSharedBath') },
    { value: 'BOTH', label: t('roomAds.fields.roomBoth') },
  ];
  const furnishingOptions = [
    { value: 'FURNISHED', label: t('roomAds.fields.furnished') },
    { value: 'UNFURNISHED', label: t('roomAds.fields.unfurnished') },
  ];
  const [selectedCityId, setSelectedCityId] = useState<number | undefined>();
  const { data: amenitiesData } = useQuery({
    queryKey: ['room-ad-form', 'amenities'],
    queryFn: () => propertyAmenityApi.list({ page: 0, size: 100, sort: ['id,asc'] }),
  });
  const { data: nationalitiesData } = useQuery({
    queryKey: ['room-ad-form', 'nationalities'],
    queryFn: () => nationalityApi.list({ page: 0, size: 100, sort: ['id,asc'] }),
  });
  const amenityOptions = (amenitiesData?.content ?? []).map((a: any) => ({ value: a.id, label: a.nameEn || a.nameAr || `#${a.id}` }));
  const nationalityOptions = (nationalitiesData?.content ?? []).map((n: any) => ({ value: n.id, label: n.countryNameEn || n.countryNameAr || `#${n.id}` }));

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        title: initialData.title || '',
        description: initialData.description || '',
        rejectReason: initialData.rejectReason || '',
        status: initialData.status || undefined,
        advertiserType: initialData.advertiserType || undefined,
        realEstateAgentType: initialData.realEstateAgentType || undefined,
        propertyType: initialData.propertyType || undefined,
        daysAvailableOption: initialData.daysAvailableOption || undefined,
        minimumStayOption: initialData.minimumStayOption || undefined,
        maximumStayOption: initialData.maximumStayOption || undefined,
        availableFrom: toDateTimeLocal(initialData.availableFrom),
        isFeatured: initialData.isFeatured ?? false,
        showPhoneNumber: initialData.showPhoneNumber ?? true,
        showWhatsappContact: initialData.showWhatsappContact ?? true,
        showOwnerInfo: initialData.showOwnerInfo ?? true,
        showInAppMessaging: initialData.showInAppMessaging ?? true,
        hideExactLocation: initialData.hideExactLocation ?? false,
        numberOfBedrooms: initialData.numberOfBedrooms ?? undefined,
        numberOfBathrooms: initialData.numberOfBathrooms ?? undefined,
        adLicenseNumber: initialData.adLicenseNumber || '',
        propertyDeedNumber: initialData.propertyDeedNumber || '',
        nationalID: initialData.nationalID || '',
        companyUnifiedNumber: initialData.companyUnifiedNumber || '',
        phonePrefix: initialData.phonePrefix || '',
        phoneNumber: initialData.phoneNumber || '',
        phoneCountryCode: initialData.phoneCountryCode || '',
        cityId: initialData.cityId ?? initialData.addressGeoCityId ?? undefined,
        districtId: initialData.districtId ?? initialData.addressGeoDistrictId ?? undefined,
        latitude: initialData.latitude ?? undefined,
        longitude: initialData.longitude ?? undefined,
        rent: initialData.roomDetails?.[0]?.rent ?? undefined,
        deposit: initialData.roomDetails?.[0]?.deposit ?? undefined,
        billType: initialData.roomDetails?.[0]?.billType || undefined,
        rentType: initialData.roomDetails?.[0]?.rentType || undefined,
        roomType: initialData.roomDetails?.[0]?.roomType || undefined,
        propertyFurnishing: initialData.roomDetails?.[0]?.propertyFurnishing || undefined,
        amenities: initialData.amenities?.map((a: any) => a.id) ?? [],
        ...prefsToFields('currentRoommates', initialData.currentRoommates),
        ...prefsToFields('preferredRoommates', initialData.preferredRoommates),
      });
      setSelectedCityId(initialData.cityId ?? initialData.addressGeoCityId ?? undefined);
    }
  }, [initialData, form]);

  const handleFinish = (values: any) => {
    const payload: Record<string, unknown> = {};
    if (values.title) payload.title = values.title;
    if (values.description) payload.description = values.description;
    if (values.rejectReason) payload.rejectReason = values.rejectReason;
    if (values.status) payload.status = values.status;
    if (values.advertiserType) payload.advertiserType = values.advertiserType;
    if (values.realEstateAgentType) payload.realEstateAgentType = values.realEstateAgentType;
    if (values.propertyType) payload.propertyType = values.propertyType;
    if (values.daysAvailableOption) payload.daysAvailableOption = values.daysAvailableOption;
    if (values.minimumStayOption) payload.minimumStayOption = values.minimumStayOption;
    if (values.maximumStayOption) payload.maximumStayOption = values.maximumStayOption;
    if (values.availableFrom) payload.availableFrom = new Date(values.availableFrom).toISOString();
    payload.isFeatured = values.isFeatured;
    payload.showPhoneNumber = values.showPhoneNumber;
    payload.showWhatsappContact = values.showWhatsappContact;
    payload.showOwnerInfo = values.showOwnerInfo;
    payload.showInAppMessaging = values.showInAppMessaging;
    payload.hideExactLocation = values.hideExactLocation;
    if (values.numberOfBedrooms != null) payload.numberOfBedrooms = Number(values.numberOfBedrooms);
    if (values.numberOfBathrooms != null) payload.numberOfBathrooms = Number(values.numberOfBathrooms);
    if (values.adLicenseNumber) payload.adLicenseNumber = values.adLicenseNumber;
    if (values.propertyDeedNumber) payload.propertyDeedNumber = values.propertyDeedNumber;
    if (values.nationalID) payload.nationalID = values.nationalID;
    if (values.companyUnifiedNumber) payload.companyUnifiedNumber = values.companyUnifiedNumber;
    if (values.phonePrefix) payload.phonePrefix = values.phonePrefix;
    if (values.phoneNumber) payload.phoneNumber = values.phoneNumber;
    if (values.phoneCountryCode) payload.phoneCountryCode = values.phoneCountryCode;
    if (values.cityId) payload.cityId = Number(values.cityId);
    if (values.districtId) payload.districtId = Number(values.districtId);
    if (values.latitude != null) payload.latitude = Number(values.latitude);
    if (values.longitude != null) payload.longitude = Number(values.longitude);
    if (values.rent != null) payload.rent = Number(values.rent);
    if (values.deposit != null) payload.deposit = Number(values.deposit);
    if (values.billType) payload.billType = values.billType;
    if (values.rentType) payload.rentType = values.rentType;
    if (values.roomType) payload.roomType = values.roomType;
    if (values.propertyFurnishing) payload.propertyFurnishing = values.propertyFurnishing;
    if (Array.isArray(values.amenities)) payload.amenities = values.amenities.map(Number);
    const currentRoommates = collectPrefs(values, 'currentRoommates');
    const preferredRoommates = collectPrefs(values, 'preferredRoommates');
    if (currentRoommates) payload.currentRoommates = currentRoommates;
    if (preferredRoommates) payload.preferredRoommates = preferredRoommates;
    onSubmit(payload);
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} style={{ maxWidth: 760 }}>
      <Typography.Title level={5}>{t('roomAds.fields.section.core')}</Typography.Title>
      <Form.Item label={t('common.title')} name="title" rules={[{ required: true, message: t('common.required', { defaultValue: 'Required' }) as string }]}>
        <Input />
      </Form.Item>

      <Form.Item label={t('roomAds.description')} name="description">
        <Input.TextArea rows={3} />
      </Form.Item>

      {!isCreate && (
        <Form.Item label={t('roomAds.rejectReason')} name="rejectReason">
          <Input.TextArea rows={2} />
        </Form.Item>
      )}

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item label={t('roomAds.status')} name="status" initialValue="DRAFT">
            <Select allowClear options={statusOptions} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t('roomAds.advertiserType')} name="advertiserType">
            <Select allowClear options={advertiserOptions} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item label={t('roomAds.propertyType')} name="propertyType">
            <Select allowClear options={propertyOptions} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t('roomAds.agentType')} name="realEstateAgentType">
            <Select allowClear options={agentOptions} />
          </Form.Item>
        </Col>
      </Row>

      <Divider />
      <Typography.Title level={5}>{t('roomAds.fields.section.location')}</Typography.Title>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label={t('roomAds.fields.city')} name="cityId">
                <CitySelect
                  placeholder={t('roomAds.fields.city')}
                  onChange={(v: any) => {
                    setSelectedCityId(v ?? undefined);
                    form.setFieldsValue({
                      cityId: v,
                      districtId: undefined,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('roomAds.fields.district')} name="districtId">
                <DistrictSelect
                  cityId={selectedCityId}
                  placeholder={t('roomAds.fields.district')}
                  disabled={!selectedCityId}
                  onChange={(v: any) => form.setFieldsValue({
                    districtId: v,
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item label={t('roomAds.fields.latitude')} name="latitude">
                <InputNumber style={{ width: '100%' }} step={0.000001} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label={t('roomAds.fields.longitude')} name="longitude">
                <InputNumber style={{ width: '100%' }} step={0.000001} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          <Typography.Title level={5}>{t('roomAds.fields.section.price')}</Typography.Title>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item label={t('roomAds.rent')} name="rent">
                <InputNumber style={{ width: '100%' }} min={0} placeholder={t('roomAds.fields.rentPlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label={t('roomAds.deposit')} name="deposit">
                <InputNumber style={{ width: '100%' }} min={0} placeholder={t('roomAds.fields.depositPlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label={t('roomAds.rentType')} name="rentType">
                <Select allowClear options={rentTypeOptions} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label={t('roomAds.billType')} name="billType">
                <Select allowClear options={billOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item label={t('roomAds.roomType')} name="roomType">
                <Select allowClear options={roomTypeOptions} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label={t('roomAds.furnishing')} name="propertyFurnishing">
                <Select allowClear options={furnishingOptions} />
              </Form.Item>
            </Col>
          </Row>

      <Divider />
      <Typography.Title level={5}>{t('roomAds.fields.section.availability')}</Typography.Title>
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item label={t('roomAds.daysAvailable')} name="daysAvailableOption">
            <Select allowClear options={daysOptions} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={t('roomAds.minStay')} name="minimumStayOption">
            <Select allowClear options={stayOptions} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={t('roomAds.maxStay')} name="maximumStayOption">
            <Select allowClear options={stayOptions} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t('roomAds.availableFrom')} name="availableFrom">
            <Input type="datetime-local" />
          </Form.Item>
        </Col>
      </Row>

      <Divider />
      <Typography.Title level={5}>{t('roomAds.fields.section.classification')}</Typography.Title>
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item label={t('roomAds.bedrooms')} name="numberOfBedrooms">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={t('roomAds.bathrooms')} name="numberOfBathrooms">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={t('roomAds.licenseNumber')} name="adLicenseNumber">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={t('roomAds.deedNumber')} name="propertyDeedNumber">
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label={t('roomAds.amenities')} name="amenities">
        <Select mode="multiple" allowClear showSearch options={amenityOptions} />
      </Form.Item>

      <Divider />
      <Typography.Title level={5}>{t('roomAds.currentRoommates')}</Typography.Title>
      <PreferenceFields prefix="currentRoommates" genderOptions={genderOptions} smokingOptions={smokingOptions} occupationOptions={occupationOptions} languageOptions={languageOptions} nationalityOptions={nationalityOptions} />

      <Divider />
      <Typography.Title level={5}>{t('roomAds.preferredRoommates')}</Typography.Title>
      <PreferenceFields prefix="preferredRoommates" genderOptions={genderOptions} smokingOptions={smokingOptions} occupationOptions={occupationOptions} languageOptions={languageOptions} nationalityOptions={nationalityOptions} />
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item label={t('roomAds.nationalId')} name="nationalID">
            <Input autoComplete="off" data-allstak-mask="true" data-sensitive="true" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t('roomAds.companyNumber')} name="companyUnifiedNumber">
            <Input autoComplete="off" data-allstak-mask="true" data-sensitive="true" />
          </Form.Item>
        </Col>
      </Row>

      <Divider />
      <Typography.Title level={5}>{t('roomAds.fields.section.phone')}</Typography.Title>
      <Row gutter={16}>
        <Col xs={8}>
          <Form.Item label={t('roomAds.phonePrefix')} name="phonePrefix">
            <Input autoComplete="tel-country-code" data-allstak-mask="true" data-sensitive="true" />
          </Form.Item>
        </Col>
        <Col xs={8}>
          <Form.Item label={t('roomAds.phoneNumber')} name="phoneNumber">
            <Input autoComplete="tel-national" data-allstak-mask="true" data-sensitive="true" />
          </Form.Item>
        </Col>
        <Col xs={8}>
          <Form.Item label={t('roomAds.phoneCountryCode')} name="phoneCountryCode">
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Divider />
      <Typography.Title level={5}>{t('roomAds.fields.section.flags')}</Typography.Title>
      <Row gutter={24}>
        <Col>
          <Form.Item label={t('roomAds.featured')} name="isFeatured" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('roomAds.showPhone')} name="showPhoneNumber" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('roomAds.showWhatsapp')} name="showWhatsappContact" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('roomAds.showOwnerInfo')} name="showOwnerInfo" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('roomAds.showInAppMessaging')} name="showInAppMessaging" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item label={t('roomAds.hideLocation')} name="hideExactLocation" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isLoading}>
          {isCreate ? t('roomAds.add') : t('common.save')}
        </Button>
      </Form.Item>
    </Form>
  );
}

function toDateTimeLocal(value?: string | null) {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 16);
}

function prefsToFields(prefix: string, prefs: any) {
  if (!prefs) return {};
  return {
    [`${prefix}MaleRoommatesCount`]: prefs.maleRoommatesCount,
    [`${prefix}FemaleRoommatesCount`]: prefs.femaleRoommatesCount,
    [`${prefix}MinAge`]: prefs.minAge,
    [`${prefix}MaxAge`]: prefs.maxAge,
    [`${prefix}NationalityId`]: prefs.nationalityId,
    [`${prefix}GenderPreference`]: prefs.genderPreference,
    [`${prefix}SmokingPreference`]: prefs.smokingPreference,
    [`${prefix}OccupationType`]: prefs.occupationType,
    [`${prefix}LanguagePreference`]: prefs.languagePreference,
  };
}

function collectPrefs(values: any, prefix: string) {
  return normalizePrefs({
    maleRoommatesCount: values[`${prefix}MaleRoommatesCount`],
    femaleRoommatesCount: values[`${prefix}FemaleRoommatesCount`],
    minAge: values[`${prefix}MinAge`],
    maxAge: values[`${prefix}MaxAge`],
    nationalityId: values[`${prefix}NationalityId`],
    genderPreference: values[`${prefix}GenderPreference`],
    smokingPreference: values[`${prefix}SmokingPreference`],
    occupationType: values[`${prefix}OccupationType`],
    languagePreference: values[`${prefix}LanguagePreference`],
  });
}

function normalizePrefs(prefs: Record<string, any>) {
  if (!prefs || Object.values(prefs).every((value) => value === undefined || value === null || value === '')) return null;
  return {
    ...prefs,
    maleRoommatesCount: prefs.maleRoommatesCount != null ? Number(prefs.maleRoommatesCount) : undefined,
    femaleRoommatesCount: prefs.femaleRoommatesCount != null ? Number(prefs.femaleRoommatesCount) : undefined,
    minAge: prefs.minAge != null ? Number(prefs.minAge) : undefined,
    maxAge: prefs.maxAge != null ? Number(prefs.maxAge) : undefined,
    nationalityId: prefs.nationalityId != null ? Number(prefs.nationalityId) : undefined,
  };
}

function PreferenceFields({ prefix, genderOptions, smokingOptions, occupationOptions, languageOptions, nationalityOptions }: any) {
  const { t } = useTranslation();
  return (
    <>
      <Row gutter={16}>
        <Col xs={12} md={6}><Form.Item label={t('roommatePreferences.maleCount')} name={`${prefix}MaleRoommatesCount`}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
        <Col xs={12} md={6}><Form.Item label={t('roommatePreferences.femaleCount')} name={`${prefix}FemaleRoommatesCount`}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
        <Col xs={12} md={6}><Form.Item label={t('roommatePreferences.minAge')} name={`${prefix}MinAge`}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
        <Col xs={12} md={6}><Form.Item label={t('roommatePreferences.maxAge')} name={`${prefix}MaxAge`}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} md={6}><Form.Item label={t('roommatePreferences.gender')} name={`${prefix}GenderPreference`}><Select allowClear options={genderOptions} /></Form.Item></Col>
        <Col xs={24} md={6}><Form.Item label={t('roommatePreferences.smoking')} name={`${prefix}SmokingPreference`}><Select allowClear options={smokingOptions} /></Form.Item></Col>
        <Col xs={24} md={6}><Form.Item label={t('roommatePreferences.occupation')} name={`${prefix}OccupationType`}><Select allowClear options={occupationOptions} /></Form.Item></Col>
        <Col xs={24} md={6}><Form.Item label={t('roommatePreferences.language')} name={`${prefix}LanguagePreference`}><Select allowClear options={languageOptions} /></Form.Item></Col>
        <Col xs={24} md={8}><Form.Item label={t('roommatePreferences.nationality')} name={`${prefix}NationalityId`}><Select allowClear showSearch options={nationalityOptions} /></Form.Item></Col>
      </Row>
    </>
  );
}
